import React, { useState, useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';
import { Layout, message, Spin } from 'antd';
import './App.css';

import HomePage from './pages/HomePage';
import ProductDetailsPage from './pages/ProductDetailsPage';
import CartPage from './pages/CartPage';
import Header from './components/Header';
import Footer from './components/Footer';
import NetworkErrorDisplay from './components/NetworkErrorDisplay';
import { fetchProducts, fetchCategories } from './services/api';
import AnnouncementModal from './pages/HomePage/AnnouncementModal';
import { getActiveAnnouncements } from './services/api';
import websocketService from './services/websocketService';

const { Content } = Layout;

function App() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cart, setCart] = useState([]);
  const [announcement, setAnnouncement] = useState(null);
  const [announcementVisible, setAnnouncementVisible] = useState(false);
  const [activeAnnouncements, setActiveAnnouncements] = useState([]); // Para limpiar vistos

  // Utilidad para manejar anuncios vistos en localStorage
  const getVistos = () => {
    try {
      return JSON.parse(localStorage.getItem('anuncios_vistos') || '[]');
    } catch {
      return [];
    }
  };
  const addVisto = (id) => {
    const vistos = getVistos();
    if (!vistos.includes(id)) {
      localStorage.setItem('anuncios_vistos', JSON.stringify([...vistos, id]));
    }
  };
  const limpiarVistosNoActivos = (anunciosActivos) => {
    const activosIds = anunciosActivos.map(a => a.id);
    const vistos = getVistos();
    const nuevosVistos = vistos.filter(id => activosIds.includes(id));
    localStorage.setItem('anuncios_vistos', JSON.stringify(nuevosVistos));
  };

  const loadInitialData = async () => {
    try {
      setLoading(true);
      setError(null); // Limpiar errores anteriores

      const [productsData, categoriesData] = await Promise.all([
        fetchProducts(),
        fetchCategories()
      ]);

      // Mapear el nombre de la categoría correcto si el backend envía null (cruzar con categoriesData)
      const mappedProducts = productsData.map(product => {
        if (!product.categoryName || product.categoryName === "Sin categoría") {
          const cat = categoriesData.find(c => c.id === product.categoryId);
          if (cat) {
            product.categoryName = cat.name;
          }
        }
        return product;
      });

      setProducts(mappedProducts);
      setCategories(categoriesData);
    } catch (error) {
      console.error("Error cargando datos iniciales:", error);
      setError(error);
      message.error(error.userMessage || "Error al cargar los datos. Por favor, intente de nuevo más tarde.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInitialData();
  }, []);

  // Consultar anuncios activos al cargar y cada 30s
  useEffect(() => {
    let interval;
    const fetchAnnouncements = async () => {
      try {
        const res = await getActiveAnnouncements();
        setActiveAnnouncements(res);
        limpiarVistosNoActivos(res);
        // Buscar el primer anuncio no visto
        const vistos = getVistos();
        const anuncioNoVisto = res.find(a => !vistos.includes(a.id));
        if (anuncioNoVisto) {
          setAnnouncement(anuncioNoVisto);
          setAnnouncementVisible(true);
        } else {
          setAnnouncement(null);
          setAnnouncementVisible(false);
        }
      } catch (e) {
        setAnnouncement(null);
        setAnnouncementVisible(false);
      }
    };
    fetchAnnouncements();
    interval = setInterval(fetchAnnouncements, 30000);
    return () => clearInterval(interval);
  }, []);

  // Conexión a WebSocket para anuncios en tiempo real
  useEffect(() => {
    websocketService.connect();
    // Escuchar evento de nuevo anuncio
    const handleNuevoAnuncio = (anuncio) => {
      // Solo mostrar si está activo y no expirado
      const ahora = new Date();
      const inicio = new Date(anuncio.fecha_inicio);
      const expiracion = new Date(anuncio.fecha_expiracion);
      if (anuncio.activo && inicio <= ahora && expiracion >= ahora) {
        const vistos = getVistos();
        if (!vistos.includes(anuncio.id)) {
          setAnnouncement(anuncio);
          setAnnouncementVisible(true);
        }
      }
    };
    if (websocketService.socket) {
      websocketService.socket.on('nuevo-anuncio', handleNuevoAnuncio);
    }
    return () => {
      if (websocketService.socket) {
        websocketService.socket.off('nuevo-anuncio', handleNuevoAnuncio);
      }
      websocketService.disconnect();
    };
  }, []);

  // Cuando se cierra el modal, marcar como visto
  const handleCloseAnnouncement = () => {
    if (announcement?.id) {
      addVisto(announcement.id);
    }
    setAnnouncementVisible(false);
  };

  const addToCart = (product, quantity = 1) => {
    setCart(prevCart => {
      const existingItem = prevCart.find(item => item.id === product.id);
      if (existingItem) {
        // Revisar stock disponible
        const nuevaCantidad = existingItem.quantity + quantity;
        const limiteStock = existingItem.stock || 999;
        const cantidadFinal = nuevaCantidad > limiteStock ? limiteStock : nuevaCantidad;

        if (existingItem.quantity >= limiteStock && quantity > 0) {
          message.warning(`Solo hay ${limiteStock} unidades disponibles de ${product.name}`);
          return prevCart;
        }

        return prevCart.map(item =>
          item.id === product.id
            ? { ...item, quantity: cantidadFinal }
            : item
        );
      } else {
        const limiteStock = product.stock || 999;
        const cantidadFinal = quantity > limiteStock ? limiteStock : quantity;
        return [...prevCart, { ...product, quantity: cantidadFinal }];
      }
    });
    message.success(`${product.name} añadido al carrito`);
  };

  const removeFromCart = (productId) => {
    setCart(prevCart => prevCart.filter(item => item.id !== productId));
    message.info("Producto eliminado del carrito");
  };

  const updateCartItemQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }

    setCart(prevCart =>
      prevCart.map(item => {
        if (item.id === productId) {
          const limiteStock = item.stock || 999;
          const cantidadFinal = quantity > limiteStock ? limiteStock : quantity;
          if (quantity > limiteStock) {
            message.warning(`Solo hay ${limiteStock} unidades disponibles`);
          }
          return { ...item, quantity: cantidadFinal };
        }
        return item;
      })
    );
  };

  // Si hay un error de carga inicial, mostrar pantalla de error con opción de reintento
  if (error && !products.length) {
    return (
      <Layout className="app-layout">
        <Header cart={cart} products={products} />
        <Content className="site-content">
          <div className="error-container">
            <NetworkErrorDisplay
              error={error}
              onRetry={loadInitialData}
              message={error.userMessage}
            />
          </div>
        </Content>
        <Footer />
      </Layout>
    );
  }

  // Mostrar spinner de carga durante la carga inicial
  if (loading && !products.length) {
    return (
      <Layout className="app-layout">
        <Header cart={[]} products={[]} />
        <Content className="site-content">
          <div className="loading-container">
            <Spin size="large" tip="Cargando catálogo...">
              <div style={{ padding: '50px' }} />
            </Spin>
            <p className="loading-message">Estamos preparando los mejores productos para ti...</p>
          </div>
        </Content>
        <Footer />
      </Layout>
    );
  }

  return (
    <Layout className="app-layout">
      {/* MODAL DE ANUNCIO GLOBAL */}
      <AnnouncementModal
        visible={announcementVisible}
        anuncio={announcement}
        onClose={handleCloseAnnouncement}
        bloqueante={announcement?.bloqueante}
      />
      <Header cart={cart} products={products} />
      <Content className="site-content">
        <Routes>
          <Route
            path="/"
            element={
              <HomePage
                products={products}
                categories={categories}
                loading={loading}
                addToCart={addToCart}
                error={error}
                onRetry={loadInitialData}
                cart={cart} // <-- Se agrega el prop cart
              />
            }
          />
          <Route
            path="/product/:id"
            element={
              <ProductDetailsPage
                products={products}
                addToCart={addToCart}
              />
            }
          />
          <Route
            path="/cart"
            element={
              <CartPage
                cart={cart}
                updateCartItemQuantity={updateCartItemQuantity}
                removeFromCart={removeFromCart}
              />
            }
          />
        </Routes>
      </Content>
      <Footer />
    </Layout>
  );
}

export default App;
